import api from './api';

export interface Registercontrole {
  kvk: { nummer: string | null; status: 'actief' | 'uitgeschreven' | 'onbekend'; gecontroleerd_op: string | null };
  verzekering: { verzekeraar: string | null; geldig_tot: string | null; status: 'geldig' | 'verlopen' | 'onbekend' };
}

export interface PaspoortStatus {
  periode: { van: string; tot: string };
  pe: {
    punten: number;
    per_register: Array<{ register: string; punten: number }>;
    posten: Array<{ titel: string; afgerond_op: string; punten: number; register: string | null }>;
  };
  intervisie: { uren: number; sessies: number; sessies_teams: number; posten: Array<{ datum: string; groep: string; uren: number; teams: boolean }> };
  praktijk: { uren: number; facturen: number; opdrachtgevers: number };
  registercontrole?: Registercontrole;
}

export interface PaspoortUitgifte {
  id: string;
  periode_start: string;
  periode_eind: string;
  pe_punten: number;
  intervisie_uren: number;
  intervisie_sessies: number;
  praktijk_uren: number;
  verificatie_url: string;
  ingetrokken: boolean;
  created_at: string;
}

export interface PaspoortVerificatie {
  status: 'geldig' | 'ingetrokken' | 'ongeldig';
  naam: string | null;
  bedrijfsnaam: string | null;
  kvk_nummer: string | null;
  rollen: Array<{ rol: string; register: string | null; nummer: string | null }>;
  registercontrole: Registercontrole | null;
  periode_start: string;
  periode_eind: string;
  pe_punten: number;
  pe_per_register: Array<{ register: string; punten: number }>;
  intervisie_uren: number;
  intervisie_sessies: number;
  praktijk_uren: number;
  uitgegeven_op: string;
  ingetrokken_op: string | null;
  ingetrokken_door: 'professional' | 'belagom' | null;
  intervisie_sessies_teams: number;
  /** per sessie; log_klopt = de post matcht de append-only aanwezigheidslog (null = handmatig of oud paspoort) */
  intervisie_posten?: Array<{ datum: string; uren: number; teams: boolean; teams_meeting_id: string | null; aanwezigheid_pct: number | null; log_klopt: boolean | null }>;
  zegel_hash: string;
  pdf_sha256: string | null;
}

const BASIS = '/professional/kwaliteitspaspoort';

export const haalStatus = async (van: string, tot: string): Promise<PaspoortStatus> =>
  (await api.get(`${BASIS}/status`, { params: { van, tot } })).data.data;

export const haalUitgiftes = async (): Promise<PaspoortUitgifte[]> => (await api.get(BASIS)).data.data;

export const trekIn = async (id: string) => api.post(`${BASIS}/${id}/intrekken`);

export const verifieer = async (code: string): Promise<PaspoortVerificatie> =>
  (await api.get(`/paspoort-verificatie/${encodeURIComponent(code)}`)).data.data;

function bewaar(blob: Blob, bestandsnaam: string) {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = bestandsnaam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Bij responseType blob komt ook de foutmelding als Blob binnen; haal de JSON-tekst eruit. */
export async function foutmelding(e: unknown, standaard: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  try {
    if (data instanceof Blob) return JSON.parse(await data.text()).message ?? standaard;
    if (data && typeof data === 'object' && 'message' in data) return String((data as { message: unknown }).message);
  } catch {
    /* geen JSON */
  }
  return standaard;
}

export interface AdminPaspoort {
  id: string;
  professional: { id: string; naam: string; email: string };
  periode_start: string;
  periode_eind: string;
  pe_punten: number;
  intervisie_uren: number;
  intervisie_sessies: number;
  praktijk_uren: number;
  verificatie_url: string;
  ingetrokken_op: string | null;
  ingetrokken_door: 'professional' | 'belagom' | null;
  ingetrokken_reden: string | null;
  created_at: string;
}

export const adminLijst = async (zoek?: string): Promise<AdminPaspoort[]> =>
  (await api.get('/admin/kwaliteitspaspoorten', { params: zoek ? { zoek } : {} })).data.data;

export const adminIntrekken = async (id: string, reden: string) => api.post(`/admin/kwaliteitspaspoorten/${id}/intrekken`, { reden });

export async function genereerPdf(van: string, tot: string) {
  const r = await api.post(`${BASIS}/genereer`, { van, tot }, { responseType: 'blob' });
  bewaar(r.data as Blob, `BeLagom-Kwaliteitspaspoort-${van}-${tot}.pdf`);
}

export async function downloadPdf(p: PaspoortUitgifte) {
  const r = await api.get(`${BASIS}/${p.id}/pdf`, { responseType: 'blob' });
  bewaar(r.data as Blob, `BeLagom-Kwaliteitspaspoort-${p.periode_start}-${p.periode_eind}.pdf`);
}
